import React, { useCallback, useEffect, useState } from 'react';
import { Box, Flex, Button, Text, Image } from 'rebass';
import PropTypes from 'prop-types';
import useSWR from 'swr';
import { LuVideo } from "react-icons/lu";
import { MdOutlineFileUpload } from "react-icons/md";
import { GoFileMedia } from "react-icons/go";
import { IoCutSharp } from "react-icons/io5";
import { fetchFile } from "@ffmpeg/util";
import { useDropzone } from 'react-dropzone';
import store from 'store';
import { VideoUploadLabel } from 'story-media/new/big-selfie-button';
import { Spinner } from 'app/loading/styled';
import { createMediaForWatchMeGrow } from 'story-media/actions';
import Modal from 'app/modal/v3/index';
import VideoPotraitOrientation from '../assets/VideoPotraitOrientation.png';
import ImagePotraitOrientation from '../assets/ImagePotraitOrientation.png';
import VideoTrimmer from './Trimmer/VideoTrimmer';
import useEntity from 'entities/hooks/use-entity';
import ResponsiveReactPlayer from 'story-media/medium-types/ResponsiveReactPlayer';
import ClipPreview from './ClipPreview';
import { getSWR } from 'utils/api';
import { useFFmpeg } from 'contexts/FFmpegContext';
import { extensionRegex, supportedVideoTypes } from 'stories/tell-a-story/FileDropperField';
import InvalidFileError from './InvalidFileError';
import { MAX_FILE_SIZE } from 'app/styles/constants';
import Warning from './Warning';
import FirstTimeUserExperience from '../FirstTimeUserExperience';

export const MediaUploadSection = ({ UploadButton, type = 'video', onClose }) => {
    const isVideo = type === 'video';
    return (
        <Flex justifyContent="center" flexDirection="column" alignItems="center" p={4} pt={0}>
            <Text
                color="#000"
                fontFamily="Poppins"
                fontSize={"22px"}
                lineHeight="165%"
                mb="30px"
            >
                {isVideo ? 'Add a video up to 10 seconds' : `Add a ${type}`}
            </Text>
            <Text textAlign="center" fontFamily="Poppins" fontSize={16} lineHeight="165%" color="#64748B" mb={3}>
                For best results please upload a {type} in <b>portrait orientation</b>
                {isVideo && <>
                    <br />
                    <br />
                    If you add a longer video, you’ll pick a 10-second clip from the first 30 seconds
                </>}
            </Text>
            <Image minWidth={[350, 400]} maxWidth={[350, 450]} src={isVideo ? VideoPotraitOrientation : ImagePotraitOrientation} />
            <Flex my="50px" sx={{ gap: "20px" }} flexDirection={['column', 'row']} justifyContent="center" alignItems="center">
                {UploadButton}
                <Button
                    onClick={onClose}
                    width="100px"
                    sx={{ borderRadius: '23px', background: '#BDBDBD', border: 0 }}
                >
                    Cancel
                </Button>
            </Flex>
        </Flex>
    );
};

const VideoRenderer = ({ video, monthId, revalidate, age, mediaFtue, setMediaFtue }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [uploadingMedia, setMediaUpload] = useState(false);
    const [initialFiles, setInitialFiles] = useState(false);
    const [tempUrlOfMedia, setTempUrlOfMedia] = useState(false);
    const [trimVideo, setTrimVideo] = useState(false);
    const [message, setMessage] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [refreshInterval, setRefreshInterval] = useState(0);
    const [dropError, setDropError] = useState([]);
    const [showMediaFtue, setShowMediaFtue] = useState(null);
    // Object URL of the just-selected local video, shown instead of the spinner while it
    // uploads/processes. Cleared when the server video finishes; lost on refresh (then the
    // processing loader takes over).
    const [localVideoUrl, setLocalVideoUrl] = useState(null);

    const { entity: clipDetails } = useEntity(video);
    const { entity: mediaDetails } = useEntity(clipDetails?.video)
    const { ffmpeg } = useFFmpeg();

    const { data } = useSWR(mediaDetails?.id ? `/v2/media/${mediaDetails?.id}` : null, getSWR, { refreshInterval });

    // The polling SWR (/v2/media/:id) returns the freshest media attributes, while the
    // normalized entity (mediaDetails) lags a beat behind. Read state + preview from the
    // poll FIRST so the finished preview shows immediately, falling back to the entity.
    const polledAttrs = data?.data?.data?.attributes;
    const mediaAttrs = polledAttrs || mediaDetails;
    const uploadStatus = polledAttrs?.state || mediaDetails?.state;
    const previewSrc = polledAttrs?.['preview-url'] || polledAttrs?.['preview-asset-url'] || mediaDetails?.['preview-url'] || previewUrl;
    const urlToPlay = (uploadStatus === 'finished' && (mediaAttrs?.['create-portrait-videos'] || mediaAttrs?.['has-a-portrait-video'])) ? mediaAttrs?.['hls-portrait'] : (mediaDetails?.['hls-np'] || mediaAttrs?.['asset-url'])

    useEffect(() => {
        if (
            uploadStatus === 'pending_upload' ||
            uploadStatus === 'uploaded' ||
            uploadStatus === 'processing'
        ) {
            setIsLoading(true);
            setRefreshInterval(5000);
        }

        if (refreshInterval !== null && uploadStatus === 'finished') {
            setPreviewUrl(polledAttrs?.['preview-url'] || polledAttrs?.['preview-asset-url'] || mediaDetails?.['preview-url']);
            setRefreshInterval(0);
            setIsLoading(false);
            setLocalVideoUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
        }
    }, [data?.data?.data?.attributes?.['preview-asset-url'], uploadStatus, setRefreshInterval]);

    const submitMedia = async (files) => {
        // Capture a local preview so we can show the selected video immediately instead
        // of a loading spinner while it uploads/processes.
        try {
            const localFile = files && files[0];
            if (localFile) {
                setLocalVideoUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(localFile); });
            }
        } catch (e) { /* noop */ }
        setIsLoading(true);
        try {
            if (mediaFtue || !store.get('wmg_ftui_watched_media'))
                setShowMediaFtue(true)

            // The upload lifecycle events that used to be posted from here are
            // gone; uploadMedium emits them, keyed on the real medium id and timed
            // to the PUT rather than to the surrounding create round-trip.
            //
            // They were mislabelled too: `clipDetails?.id` is the
            // WatchMeGrowVideoClip, not the Medium, so each one recorded
            // 'subject-type': 'Medium' against a clip id and pointed at a medium
            // that does not exist.
            await createMediaForWatchMeGrow(monthId, files, false, {
                'owner-type': 'Books::WatchMeGrowVideoClip',
                'owner-id': clipDetails?.id,
            });
            revalidate();
            setTempUrlOfMedia(false);
            setInitialFiles(false);
            setMediaUpload(false);

        }
        catch (error) {
            // No tracked event here any more. uploadMedium reports upload failures
            // against the right subject; this catch also covers the revalidate and
            // the state updates below it, so posting video_upload_error_occurred
            // from here recorded non-upload failures as upload errors.
            //
            // It also spread the error into the event context, and an axios error
            // carries `config` with the Authorization header -- which is how live
            // session tokens ended up in events.context in production.
            console.error("Error uploading media:", error);
            onClose()
        }
    }

    const onClose = () => {
        setMediaUpload(false)
        setTempUrlOfMedia(false);
        setInitialFiles(false);
        setIsLoading(false);
        setTrimVideo(false);
    }

    const convertTo30Seonds = async (file) => {
        ffmpeg?.writeFile(file.name, await fetchFile(file));
        await ffmpeg?.exec(['-i', file.name, '-ss', '00:00:00', '-to', '00:00:30', '-acodec',
            'copy',
            '-vcodec',
            'copy',
            `out.mp4`,
        ]);

        const data = await ffmpeg?.readFile('out.mp4');

        // ✅ Safely convert whatever ffmpeg returns into a Uint8Array
        const getSafeBuffer = (data) => {
            // ✅ Already a Uint8Array — use its buffer directly
            if (data instanceof Uint8Array) {
                return data.buffer;
            }

            // ✅ Its some other ArrayBuffer type
            if (data instanceof ArrayBuffer) {
                return data;
            }

            // ✅ String returned — encode it back to binary
            if (typeof data === 'string') {
                const encoder = new TextEncoder();
                return encoder.encode(data).buffer;
            }

            // ✅ Has a buffer property (other typed arrays like Int8Array etc)
            if (data?.buffer instanceof ArrayBuffer) {
                return data.buffer;
            }

            throw new Error(`Unexpected ffmpeg readFile output type: ${typeof data}`);
        };

        try {
            const safeBuffer = getSafeBuffer(data);
            const blob = new Blob([safeBuffer], { type: 'video/mp4' });
            const url = URL.createObjectURL(blob);
            const newFile = new File([blob], file.name, { type: file.type });
            setTempUrlOfMedia({
                'duration-in-seconds': 30,
                'asset-url': url,
            });
            setInitialFiles([newFile]);

        } catch (err) {
            console.error('Failed to process ffmpeg output:', err);
            const url = URL.createObjectURL(file);
            setTempUrlOfMedia({
                'duration-in-seconds': null,
                'asset-url': url,
            });
            setInitialFiles([file]);
        }

        finally {

            try {
                await ffmpeg?.deleteFile(file.name);
                await ffmpeg?.deleteFile('out.mp4');
            } catch (cleanupErr) {
                console.warn('ffmpeg cleanup failed:', cleanupErr);
            }
        }
    }

    const handleChange = useCallback(
        (e, transcribe = false) => {
            const file = e[0];
            if (file.size >= MAX_FILE_SIZE) {
                setMessage('Please upload a video less than 2GB');
                return;
            }
            setDropError([]);
            let filteredResp = e.filter((f) => {
                const fileExtensions = extensionRegex.exec(f.name.toLowerCase());
                return (
                    f.type.includes('video') && supportedVideoTypes.includes(fileExtensions[0].toLowerCase()))
            });
            if (filteredResp.length === e.length && file) {
                const videoElement = document.createElement('video');
                videoElement.src = URL.createObjectURL(file);

                videoElement.onloadedmetadata = () => {
                    const duration = videoElement.duration;

                    if (duration <= 10) {
                        submitMedia(e);
                    }
                    if (duration > 30) {
                        convertTo30Seonds(file)
                    }
                    else {
                        setTempUrlOfMedia({
                            'duration-in-seconds': duration,
                            'asset-url': videoElement.src
                        })
                        setInitialFiles(e)
                    }
                };
                videoElement.load();
            }
            else {
                const b = new Set(filteredResp);
                const differenceOfArray = [...e].filter((x) => !b.has(x));
                setDropError(differenceOfArray);
            }
        },
        []
    );

    const { getRootProps, getInputProps } = useDropzone({ onDrop: (e) => handleChange(e, true) })

    if (isLoading) {
        return (
            <Box width={["122px", "153px"]} height={["149.661px", "188.128px"]}
                sx={{ borderRadius: 12, border: '1px dashed #9B59B6', overflow: 'hidden', position: 'relative', bg: '#F5EEF8' }}
            >
                {localVideoUrl ? (
                    <>
                        <video
                            src={localVideoUrl}
                            // iOS/Safari won't render a video's first frame until it plays,
                            // and React's `muted` prop isn't reliably applied to the DOM
                            // property (which iOS needs for muted autoplay). Force muted via
                            // ref and autoplay muted/looping inline so the preview shows on
                            // every device while the real video uploads/processes.
                            ref={(el) => { if (el) { el.muted = true; el.defaultMuted = true; } }}
                            muted
                            playsInline
                            preload="auto"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        {/* subtle "still processing" indicator over the local preview */}
                        <Flex sx={{
                            position: 'absolute', bottom: '6px', right: '6px', bg: 'rgba(0,0,0,0.45)',
                            borderRadius: '50%', p: '5px', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <Spinner color="#fff" height={"16px"} width={"16px"} />
                        </Flex>
                    </>
                ) : (
                    <Flex width="100%" height="100%" justifyContent="center" alignItems="center" color="#64748B">
                        <Spinner color="#9B59B6" height={"44px"} width={"44px"} />
                    </Flex>
                )}
            </Box>
        );
    }
    const isTrimed = clipDetails?.['trim-start'] !== null || clipDetails?.['trim-end'] !== null

    return <Box>
        {showMediaFtue && <FirstTimeUserExperience isForMedia setShowFTUE={() => {
            setShowMediaFtue(false)
            setMediaFtue(false)
            store.set('wmg_ftui_watched_media', true)
        }} />}
        {uploadingMedia && <Modal
            maxWidth={initialFiles ? 700 : 580}
            shadeProps={{ pt: [0, 4] }}
            closeButton
            close={{ onClose }}
            childSx={{ marginTop: '-40px', zIndex: 1 }}
            // aboveTooltip
            closeOnOutsideClick
        >
            <InvalidFileError
                errorFiles={dropError}
                onDismiss={() => setDropError([])}
                supportedTypes={['video']}
            />
            {message && <Warning message={message} onClose={() => setMessage(null)} />}
            {(initialFiles || trimVideo) ? <Flex justifyContent="center" flexDirection="column" alignItems="center" p={4} pt={0}>
                <Text
                    color="#000"
                    fontFamily="Poppins"
                    fontSize={"22px"}
                    lineHeight="165%"
                    mb={3}
                >
                    Trim Video
                </Text>
                <Text fontFamily="Poppins" fontSize={16} lineHeight="165%" color="#64748B" mb={3}>
                    Slide to select 10 seconds
                </Text>
                <VideoTrimmer urlToPlay={urlToPlay} onClose={onClose} isForEdit={trimVideo} monthId={monthId} id={clipDetails?.id} clipDetails={clipDetails} video={tempUrlOfMedia} onSubmit={() => submitMedia(initialFiles)} />
            </Flex>
                : !!urlToPlay ?
                    <Flex justifyContent="center" flexDirection="column" alignItems="center" p={4} pt={0}>
                        <Text
                            color="#000"
                            fontFamily="Poppins"
                            fontSize={"22px"}
                            lineHeight="165%"
                            textAlign="center"
                            mb="30px"
                        >
                            {age}
                        </Text>
                        {isTrimed ? <ClipPreview
                            isForEdit
                            id={clipDetails?.id}
                            clipDetails={clipDetails}
                            mediaDetails={mediaDetails}
                        /> :
                            <Box width={"300px"} minHeight="300px" sx={{ borderRadius: '11.559px', overflow: 'hidden' }}>
                                <ResponsiveReactPlayer
                                    preload={true}
                                    playerWrapper={{ width: '300px', height: '300px', }}
                                    url={urlToPlay}
                                    config={{
                                        file: {
                                            attributes: {
                                                preload: true
                                            }
                                        },
                                    }}
                                />
                            </Box>
                        }
                        <Flex my="50px" sx={{ gap: "20px" }} flexDirection={['column', 'row']} justifyContent="center" alignItems="center">
                            {(isTrimed) &&
                                <Flex flex={1} sx={{ cursor: 'pointer' }} alignItems="center" justifyContent="center" gap={2} onClick={() => setTrimVideo(true)}>
                                    <Text as={IoCutSharp} color="#9B59B6" size={24} />
                                    <Text fontFamily="Roboto" ml="4px" fontSize={16} lineHeight="24px" color="#9B59B6" >Retrim Video</Text>
                                </Flex>}
                            <Button bg="transparent" sx={{ border: 0 }} {...getRootProps()}>
                                <VideoUploadLabel htmlFor="selfie_video" width="100%">
                                    <input
                                        {...getInputProps()}
                                        accept="video/*"
                                    />
                                    <Flex flex={1} alignItems="center" justifyContent="center" gap={2}>
                                        <Text as={GoFileMedia} color="#9B59B6" size={24} />
                                        <Text fontFamily="Roboto" ml="4px" fontSize={16} lineHeight="24px" color="#9B59B6" >Replace Video</Text>
                                    </Flex>
                                </VideoUploadLabel>
                            </Button>

                            <Button
                                onClick={onClose}
                                width="100px"
                                height={"40px"}
                                p="0 20px"
                                sx={{ borderRadius: '23px', background: '#BDBDBD', border: 0 }}
                            >
                                Cancel
                            </Button>
                        </Flex>
                    </Flex>
                    :
                    <MediaUploadSection
                        isForEdit={!!urlToPlay}
                        UploadButton={
                            <Button
                                display="flex"
                                fontWeight="normal"
                                textAlign="center"
                                width="180px"
                                sx={{ borderRadius: '99px', border: '0px solid rgba(0, 0, 0, 0.20)' }}
                                {...getRootProps()}
                            >
                                <VideoUploadLabel htmlFor="selfie_video" width="100%">
                                    <input
                                        {...getInputProps()}
                                        accept="video/*"
                                    />
                                    <Flex flex={1} alignItems="center" justifyContent="center" gap={2}>
                                        <Text m="auto" as={MdOutlineFileUpload} color="white" size={24} />
                                        <Text fontFamily="Poppins" fontSize={16} lineHeight="24px" color="white" >Upload Video</Text>
                                    </Flex>
                                </VideoUploadLabel>
                            </Button>
                        }
                        onClose={onClose}
                    />}
        </Modal>
        }

        {previewSrc ? (
            <Box width={["122px", "153px"]} height={["149.661px", "188.128px"]} sx={{ borderRadius: '11.559px', overflow: 'hidden' }}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setMediaUpload(true)
                }}>
                <Image
                    src={previewSrc}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '11.559px'
                    }}
                />
                <Box mt="-45px">
                    <svg xmlns="http://www.w3.org/2000/svg" width="38" height="39" viewBox="0 0 38 39" fill="none">
                        <path d="M-0.000488281 0.982422H26.1457C32.5326 0.982422 37.7103 6.16009 37.7103 12.5471V38.6932H11.5642C5.17718 38.6932 -0.000488281 33.5156 -0.000488281 27.1286V0.982422Z" fill="white" fill-opacity="0.7" />
                        <path d="M27.8961 15.7137C27.5907 15.5762 27.2516 15.5315 26.921 15.5851C26.5905 15.6387 26.2829 15.7883 26.0366 16.0153L23.8756 18.0255V16.5681C23.8756 15.7684 23.5579 15.0014 22.9924 14.4359C22.4269 13.8704 21.66 13.5527 20.8603 13.5527H11.8142C11.0145 13.5527 10.2475 13.8704 9.68201 14.4359C9.11652 15.0014 8.79883 15.7684 8.79883 16.5681V24.609C8.79883 25.4088 9.11652 26.1757 9.68201 26.7412C10.2475 27.3067 11.0145 27.6244 11.8142 27.6244H20.8603C21.66 27.6244 22.4269 27.3067 22.9924 26.7412C23.5579 26.1757 23.8756 25.4088 23.8756 24.609V23.1516L26.0467 25.1619C26.3663 25.4512 26.7815 25.6123 27.2126 25.6142C27.4519 25.6136 27.6883 25.5622 27.9061 25.4634C28.2026 25.3435 28.4566 25.1379 28.6356 24.8729C28.8146 24.6079 28.9106 24.2956 28.9113 23.9758V17.2013C28.9098 16.8804 28.8123 16.5672 28.6314 16.3021C28.4505 16.037 28.1944 15.8321 27.8961 15.7137ZM21.8654 24.609C21.8654 24.8756 21.7595 25.1313 21.571 25.3198C21.3825 25.5083 21.1268 25.6142 20.8603 25.6142H11.8142C11.5476 25.6142 11.292 25.5083 11.1035 25.3198C10.915 25.1313 10.8091 24.8756 10.8091 24.609V16.5681C10.8091 16.3015 10.915 16.0459 11.1035 15.8574C11.292 15.6689 11.5476 15.563 11.8142 15.563H20.8603C21.1268 15.563 21.3825 15.6689 21.571 15.8574C21.7595 16.0459 21.8654 16.3015 21.8654 16.5681V24.609ZM26.891 23.2019L24.0666 20.5886L26.891 17.9753V23.2019Z" fill="#9B59B6" />
                    </svg>
                </Box>
            </Box>
        ) :
            <Box width={["122px", "153px"]} height={["149.661px", "188.128px"]} onClick={() => setMediaUpload(true)}>
                <Flex
                    fontWeight="normal"
                    fontSize={2}
                    flex={1}
                    height="100%"
                    justifyContent="center"
                    flexDirection="column"
                    alignItems={'center'}
                    textAlign="center"
                    p={3}
                    bg="#F5EEF8"
                    color="#64748B"
                    sx={{ borderRadius: 12, cursor: 'pointer', border: '1px dashed #9B59B6' }}
                    onClick={() => setMediaUpload(true)}
                >
                    <Text as={LuVideo} color="#64748B" size={24} />
                    <Text fontFamily="Poppins" fontSize={16} lineHeight="24px" color="#64748B" >Video</Text>
                </Flex>
            </Box>
        }
    </Box >
};

VideoRenderer.propTypes = {
    video: PropTypes.string,
};

export default VideoRenderer;